import React, { useState, useEffect } from "react";
import { Spinner } from "@chakra-ui/react";
import { FiTrash2, FiRotateCcw } from "react-icons/fi";
import { Button } from "newui/components/Button";
import { EditableCell } from "./EditableCell";
import type {
  ValidateImportResult,
  ImportClarificationRow,
  ClarificationType,
  ImportClarificationsResult,
} from "../../types/clarification.type";
import {
  CLARIFICATION_IMPACT,
  CLARIFICATION_IMPACT_LABELS,
} from "../../constants/clarification.constants";
import { Modal } from "newui/components/ui/Modal";
import { ImportHeader } from "./ImportHeader";

interface ImportValidationModalProps {
  clarificationTypes: ClarificationType[];
  isOpen: boolean;
  rfxSubmissionId: number;
  validationResult: ValidateImportResult | null;
  onClose: () => void;
  onImport: (rows: ImportClarificationRow[]) => Promise<ImportClarificationsResult | null>;
}

export const ImportValidationModal: React.FC<ImportValidationModalProps> = ({
  isOpen,
  onClose,
  onImport,
  validationResult,
  clarificationTypes,
}) => {
  const [rows, setRows] = useState<ImportClarificationRow[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [showCloseWarning, setShowCloseWarning] = useState(false);

  useEffect(() => {

    if (!validationResult || !isOpen) {
      return;
    }

    // Normalize error column names when loading rows
    const normalizedRows = validationResult.rows.map((row) => {
      if (row.errors) {
        const normalizedErrors = { ...row.errors };
        // If errors exist under clarification_type_id, copy them to clarification_type
        if (normalizedErrors.clarification_type_id) {
          normalizedErrors.clarification_type = normalizedErrors.clarification_type_id;
          delete normalizedErrors.clarification_type_id;
        }
        return { ...row, errors: normalizedErrors };
      }
      return row;
    });
    setRows(normalizedRows);
    setHasChanges(false);

  }, [validationResult, isOpen]);

  // Validates a single field value - used for revalidation after edits
  const validateField = (field: keyof ImportClarificationRow, value: any): string[] | undefined => {
    const errors: string[] = [];

    switch (field) {
      case "clarification_type":
      case "clarification_type_id":
        if (!value) {
          errors.push("Type is required");
        }
        break;

      case "clarification_text":
        if (!value || String(value).trim() === "") {
          errors.push("Clarification text is required");
        }
        break;

      case "owner":
        if (!value || String(value).trim() === "") {
          errors.push("Owner is required");
        }
        break;

      // Optional fields - no validation needed
      case "relates_to":
      case "impact":
        break;
    }

    return errors.length > 0 ? errors : undefined;
  };

  // Validates entire row - used for revalidation after edits
  const validateRow = (row: ImportClarificationRow): Record<string, string[]> | undefined => {
    const errors: Record<string, string[]> = {};

    const typeErrors = validateField("clarification_type_id", row.clarification_type_id);
    if (typeErrors) {
      errors.clarification_type = typeErrors;
    }

    const textErrors = validateField("clarification_text", row.clarification_text);
    if (textErrors) {
      errors.clarification_text = textErrors;
    }

    const ownerErrors = validateField("owner", row.owner);
    if (ownerErrors) {
      errors.owner = ownerErrors;
    }

    return Object.keys(errors).length > 0 ? errors : undefined;
  };

  const handleCellChange = (rowIndex: number, field: keyof ImportClarificationRow, value: string | number) => {
    setRows((prevRows) => {
      const newRows = [...prevRows];
      const row = { ...newRows[rowIndex] };

      if (field === "clarification_type") {
        const selectedType = clarificationTypes.find((t) => t.id === Number(value));
        if (selectedType) {
          row.clarification_type = selectedType.name;
          row.clarification_type_id = selectedType.id;
        }
      } else if (field === "clarification_text" || field === "owner") {
        row[field] = String(value);
      } else if (field === "relates_to" || field === "impact") {
        row[field] = value ? String(value) : undefined;
      } else if (field === "clarification_type_id") {
        row[field] = Number(value);
      }

      // Client-side revalidation after edit
      // This replaces the simple error clearing with full validation
      row.errors = validateRow(row);

      newRows[rowIndex] = row;
      return newRows;
    });
    setHasChanges(true);
  };

  const handleDeleteRow = (rowIndex: number) => {
    setRows((prevRows) => {
      const newRows = [...prevRows];
      const row = { ...newRows[rowIndex] };
      row.deleted = !row.deleted;
      newRows[rowIndex] = row;
      return newRows;
    });
    setHasChanges(true);
  };

  const handleClose = () => {
    if (hasChanges) {
      setShowCloseWarning(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowCloseWarning(false);
    onClose();
  };

  const handleImport = async () => {
    setIsImporting(true);
    try {
      // Filter out deleted rows before importing
      const activeRows = rows.filter((r) => !r.deleted);
      const result = await onImport(activeRows);
      if (result) {
        if (result.errors.length === 0) {
          onClose();
        } else {
          const errorMap: Record<number, Record<string, string[]>> = {};
          result.errors.forEach((error) => {
            if (!errorMap[error.row]) {
              errorMap[error.row] = {};
            }
            // Normalize column name for type field
            const columnName = error.column === "clarification_type_id" ? "clarification_type" : error.column;
            if (!errorMap[error.row][columnName]) {
              errorMap[error.row][columnName] = [];
            }
            errorMap[error.row][columnName].push(error.message);
          });

          setRows((prevRows) =>
            prevRows.map((row, idx) => ({
              ...row,
              errors: errorMap[row.row_number] || row.errors,
            }))
          );
        }
      }
    } finally {
      setIsImporting(false);
    }
  };

  const activeRows = rows.filter((r) => !r.deleted);
  const newCount = activeRows.filter((r) => r.is_new).length;
  const updateCount = activeRows.filter((r) => !r.is_new).length;
  const errorCount = activeRows.filter((r) => r.errors && Object.keys(r.errors).length > 0).length;
  const hasErrors = errorCount > 0;
  const deletedCount = rows.filter((r) => r.deleted).length;

  const impactOptions = [
    { value: "", label: "None" },
    ...Object.entries(CLARIFICATION_IMPACT).map(([key, value]) => ({
      value,
      label: CLARIFICATION_IMPACT_LABELS[value],
    })),
  ];

  impactOptions.unshift({ value: null, label: 'Select an Option' });

  const typeOptions = clarificationTypes.map((type) => ({
    value: type.id,
    label: type.name,
  }));

  typeOptions.unshift({ value: null, label: 'Select an Option' });

  const FooterComponent = () => {
    return (
      <div className="flex w-full items-center justify-between">
        <div className="text-sm text-gray-600">
          {activeRows.length} rows ready
          {deletedCount > 0 && `, ${deletedCount} deleted`}
          {errorCount > 0 && `, ${errorCount} errors`}
        </div>
        <div className="flex gap-2">
          <Button onClick={handleClose} btntype="base" disabled={isImporting}>
            Cancel
          </Button>
          <Button onClick={handleImport} disabled={hasErrors || isImporting || activeRows.length === 0}>
            {isImporting ? <Spinner size="sm" /> : "Import Data"}
          </Button>
        </div>
      </div>
    );
  };

  const CloseWarningModal = () => {
    if (!showCloseWarning) {
      return null;
    }

    return (
      <Modal
        isOpen={showCloseWarning}
        onClose={() => setShowCloseWarning(false)}
        title="Unsaved Changes"
        iconType="warning"
        modalType="alert"
        // header={{
        //   icon: "alert-triangle",
        //   bgColor: "bg-yellow-50",
        //   iconColor: "text-yellow-600",
        //   title: "",
        //   description: "",
        // }}
        size="md"
        customFooter={
          <div className="flex w-full justify-between gap-2">
            <Button onClick={() => setShowCloseWarning(false)} btntype="base" className="!w-full">
              Keep Editing
            </Button>
            <Button onClick={handleConfirmClose} btntype="danger" className="!w-full">
              Discard
            </Button>
          </div>
        }
      >
        <p>
          You have unsaved changes. Are you sure you want to discard them?
        </p>
        <p className="text-sm text-gray-700">
          All changes made to the import data will be lost if you close this dialog.
        </p>
      </Modal>
    );
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        description={`${newCount} new, ${updateCount} updates${errorCount > 0 ? `, ${errorCount} errors` : ""} - Make any required changes in the table below.`}
        title="Review Import Data"
        // header={{
        //   icon: "file-text",
        //   bgColor: hasErrors ? "bg-yellow-50" : "bg-green-50",
        //   iconColor: hasErrors ? "text-yellow-600" : "text-green-600",
        //   title: "Review Import Data",
        //   description: ,
        // }}
        size="xxl"
        customFooter={FooterComponent()}
      >
        <div className="max-h-[600px] overflow-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="sticky top-0 bg-gray-50">
              <tr>
                <ImportHeader text="Row" />
                <ImportHeader text="Ref" />
                <ImportHeader text="Type" />
                <ImportHeader text="Clarification" />
                <ImportHeader text="Relates To" />
                <ImportHeader text="Owner" />
                <ImportHeader text="Impact" />
                <ImportHeader text="Actions" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {rows.map((row, index) => {
                const hasRowErrors = row.errors && Object.keys(row.errors).length > 0;
                const isDeleted = row.deleted;
                const bgColor = isDeleted
                  ? "bg-gray-100"
                  : hasRowErrors
                    ? "bg-red-50"
                    : row.is_new
                      ? "bg-green-50"
                      : "bg-blue-50";
                const textStyle = isDeleted ? "line-through opacity-50" : "";

                return (
                  <tr key={index} className={bgColor}>
                    <td className={`whitespace-nowrap px-3 py-2 text-sm text-gray-900 ${textStyle}`}>
                      {row.row_number}
                    </td>
                    <td className={`whitespace-nowrap px-3 py-2 text-sm ${textStyle}`}>
                      <span
                        className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${row.is_new
                          ? "bg-green-100 text-green-800"
                          : "bg-blue-100 text-blue-800"
                          }`}
                      >
                        {row.is_new ? "New" : "Update"}
                      </span>
                    </td>
                    <td className={`px-3 py-2 text-sm ${textStyle}`}>
                      {isDeleted ? (
                        <span className="text-gray-500">{row.clarification_type}</span>
                      ) : (
                        <EditableCell
                          value={row.clarification_type_id}
                          displayValue={row.clarification_type}
                          onChange={(value) => handleCellChange(index, "clarification_type", value)}
                          error={row.errors?.clarification_type}
                          type="select"
                          options={typeOptions}
                          required
                        />
                      )}
                    </td>
                    <td className={`max-w-xs px-3 py-2 text-sm ${textStyle}`}>
                      {isDeleted ? (
                        <span className="text-gray-500">{row.clarification_text}</span>
                      ) : (
                        <EditableCell
                          value={row.clarification_text}
                          onChange={(value) => handleCellChange(index, "clarification_text", value)}
                          error={row.errors?.clarification_text}
                          type="textarea"
                          required
                        />
                      )}
                    </td>
                    <td className={`px-3 py-2 text-sm ${textStyle}`}>
                      {isDeleted ? (
                        <span className="text-gray-500">{row.relates_to || "-"}</span>
                      ) : (
                        <EditableCell
                          value={row.relates_to || ""}
                          onChange={(value) => handleCellChange(index, "relates_to", value)}
                          error={row.errors?.relates_to}
                          type="text"
                        />
                      )}
                    </td>
                    <td className={`px-3 py-2 text-sm ${textStyle}`}>
                      {isDeleted ? (
                        <span className="text-gray-500">{row.owner}</span>
                      ) : (
                        <EditableCell
                          value={row.owner}
                          onChange={(value) => handleCellChange(index, "owner", value)}
                          error={row.errors?.owner}
                          type="text"
                          required
                        />
                      )}
                    </td>
                    <td className={`px-3 py-2 text-sm ${textStyle}`}>
                      {isDeleted ? (
                        <span className="text-gray-500">
                          {row.impact ? CLARIFICATION_IMPACT_LABELS[row.impact] : "None"}
                        </span>
                      ) : (
                        <EditableCell
                          value={row.impact || ""}
                          displayValue={row.impact ? CLARIFICATION_IMPACT_LABELS[row.impact] : "None"}
                          onChange={(value) => handleCellChange(index, "impact", value)}
                          error={row.errors?.impact}
                          type="select"
                          options={impactOptions}
                        />
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-center text-sm">
                      <button
                        onClick={() => handleDeleteRow(index)}
                        className={isDeleted ? "text-blue-600 hover:text-blue-800" : "text-red-600 hover:text-red-800"}
                        title={isDeleted ? "Restore row" : "Delete row"}
                      >
                        {isDeleted ? (
                          <FiRotateCcw className="h-4 w-4" />
                        ) : (
                          <FiTrash2 className="h-4 w-4" />
                        )}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Modal>

      <CloseWarningModal />
    </>
  );
};
